import { html, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { PopBaseComponent } from '../../base/pop-base-component';
import { Constants as BaseConstants } from '../../base/base.constants.js';
import style from './contact-us.scss?inline';
import type { ContactUs, Promo } from './contact-us.model';
import { Constants, VehicleSubjectLines } from './contact-us.constants';

/**
 * @tag pop-contact-us-component
 * @property {contact-us} [componentObject] contact-us content
 * @attribute {contact-us} [data-component-object] contact-us content
 */
@customElement(Constants.TAG_NAME)
export class PopContactUsComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  /**
   * The component object that contains the pre-fetched data to be rendered.
   */
  @property({
    attribute: BaseConstants.DATA_COMPONENT_OBJECT,
    type: Object,
  })
  componentObject?: ContactUs;

  /**
   * Vehicle Code
   * @type string
   */
  @property({ attribute: BaseConstants.DATA_VEHICLE, type: String })
  vehicleCode?: string;

  /**
   * @internal
   */
  static readonly tagName = Constants.TAG_NAME;

  /**
   * @internal
   */
  componentName = PopContactUsComponent.tagName;

  renderEmailIcon() {
    return html`<beacon-icon slot="prefix" name="email"></beacon-icon>`;
  }

  renderPhoneIcon() {
    return html`<beacon-icon slot="prefix" name="phone"></beacon-icon>`;
  }

  renderCalendarIcon() {
    return html`<beacon-icon slot="prefix" name="calendar"></beacon-icon>`;
  }

  getSubjectLineForVehicleCode(code?: string): string {
    if (code) {
      return VehicleSubjectLines[code] ?? Constants.NO_VEHICLE_SUBJECT_LINE;
    }

    return Constants.NO_VEHICLE_SUBJECT_LINE;
  }

  renderTopSection() {
    const subject = this.getSubjectLineForVehicleCode(this.vehicleCode);
    const description = Constants.EMAIL_BODY_TEXT;
    const contactEmail = Constants.CONTACT_EMAIL;

    const mailtoLink = `mailto:${contactEmail}?subject=${subject}&body=${description}`;

    return html`
      <div class="contact-us-top-section b-padding-block-end--micro--jumbo">
        <div
          class="contact-us-phone-container b-typography-sans-serif--body-loose--100 b-font-weight-bold"
        >
          <beacon-button
            variant="tertiary"
            scale="normal"
            link-href="${Constants.TEL_LINK}${Constants.CONTACT_NUMBER}"
          >
            ${Constants.CONTACT_NUMBER} ${this.renderPhoneIcon()}
          </beacon-button>
        </div>
        <beacon-button
          variant="tertiary"
          scale="normal"
          link-href="${mailtoLink}"
          link-target="_blank"
        >
          ${Constants.CONTACT_EMAIL} ${this.renderEmailIcon()}
        </beacon-button>
      </div>
    `;
  }

  renderButtonSection() {
    return html`
      <div class="contact-us-button-section b-margin-block--micro--jumbo">
        <beacon-button
          variant="secondary"
          scale="normal"
          link-href="${Constants.SCHEDULER_BUTTON_LINK}"
          link-target="_blank"
        >
          ${Constants.SCHEDULER_BUTTON_LABEL} ${this.renderCalendarIcon()}
        </beacon-button>
      </div>
    `;
  }

  renderPromoTile(promo: Promo) {
    return html`
      <beacon-article-tile
        class="contact-us-promo-block"
        layout="standard"
        position="vertical"
        scale="normal"
      >
        <div slot="body">
          <h3 class="beacon-article-tile__title">${promo.title}</h3>
          <p class="beacon-article-tile__description">${promo.description}</p>
        </div>
        <div slot="footer" class="beacon-article-tile__footer">
          <beacon-button
            variant="tertiary"
            scale="normal"
            link-href="${promo.link}"
            link-target="_blank"
          >
            ${promo.linkText}
          </beacon-button>
        </div>
      </beacon-article-tile>
    `;
  }

  renderPromoSection() {
    const promos = this.componentObject?.promos;

    if (promos && promos.length > 0) {
      return html`<div
        class="contact-us-promo-container b-margin-block--micro--jumbo"
      >
        ${promos.map((promo) => {
          return this.renderPromoTile(promo);
        })}
      </div>`;
    }

    return html``;
  }

  render() {
    return this.renderComponent(html`
      <section class="contact-us-content b-container">
        ${this.renderTopSection()} ${this.renderButtonSection()}
        ${this.renderPromoSection()}
      </section>
    `);
  }
}
